import { avatarColorIndex, nameInitials } from '@/components/ui/avatar';

import type { AuditLogRow } from './types';
import { EMPTY_DATE, formatInstantDate, formatTime } from '@/lib/time';

// Presentación pura de un evento de la bitácora (sin React, testeable directo).
// La acción y el cambio llegan en palabras desde el API (única fuente, la misma de los
// informes exportados); aquí se resuelven actor, origen, severidad y fecha.

export type BadgeTone = 'info' | 'warning' | 'critical';

const TONES: BadgeTone[] = ['info', 'warning', 'critical'];

/** Tono de badge acotado; cualquier severidad fuera de la unión cae a info. */
export function severityTone(severity: string): BadgeTone {
  return TONES.includes(severity as BadgeTone) ? (severity as BadgeTone) : 'info';
}

function isSystem(row: AuditLogRow): boolean {
  return row.actorType === 'system';
}

/** Nombre del actor: nombre → email → authSubject; "Sistema" para eventos de sistema. */
export function actorLabel(row: AuditLogRow): string {
  if (row.actorDisplayName) {
    return row.actorDisplayName;
  }
  if (row.actorEmail) {
    return row.actorEmail;
  }
  if (row.actorAuthSubject) {
    return row.actorAuthSubject;
  }
  return isSystem(row) ? 'Sistema' : '—';
}

/** Iniciales para el avatar: SYS para sistema; si no, hasta 2 letras del nombre. */
export function actorInitials(row: AuditLogRow): string {
  if (isSystem(row) && !row.actorDisplayName) {
    return 'SYS';
  }
  return nameInitials(row.actorDisplayName ?? row.actorEmail ?? '');
}

/** Origen del evento: IP; "Tarea programada" para sistema sin IP; guion si no hay dato. */
export function originLabel(row: AuditLogRow): string {
  if (row.ip) {
    return row.ip;
  }
  return isSystem(row) ? 'Tarea programada' : '—';
}

/** Índice de color estable (1..8) para el avatar del actor; 0-como-neutro para
 *  sistema (la UI le da un gris). Deriva de una identidad estable del actor. */
export function actorColorIndex(row: AuditLogRow): number {
  if (isSystem(row) && !row.actorDisplayName) {
    return 0;
  }
  return avatarColorIndex(row.actorAuthSubject ?? row.actorDisplayName ?? row.actorEmail ?? '');
}

/** Fecha y hora del evento en la zona de negocio, en dos líneas (formato canónico, la
 *  hora al segundo). Una fecha inválida degrada al ISO crudo en vez de romper. */
export function formatWhen(iso: string): { date: string; time: string } {
  const date = formatInstantDate(iso);
  return date === EMPTY_DATE
    ? { date: iso, time: '' }
    : { date, time: formatTime(iso, { seconds: true }) };
}

/** Valor antes/después de un cambio (estado, rol, cláusula, código), en palabras del API. */
export function beforeAfter(row: AuditLogRow): { before: string | null; after: string | null } {
  return { before: row.beforeLabel, after: row.afterLabel };
}
